import { Component, Fragment } from "react";
import { inject, observer } from "mobx-react";
import moment from 'moment/min/moment-with-locales';
import { OverlayTrigger, Tooltip, Row, Col } from 'react-bootstrap';
import { confirmAlert } from 'react-confirm-alert';

// components
import applicationRouter from '~/hoc/applicationRouter';
import withLocalization from '~/hoc/withLocalization';
import GenericList from '~/components/GenericList/GenericList';
import GenericFilter from '~/components/GenericList/GenericFilter';

// elements
import Button from '~/components/CustomButton/CustomButton.jsx';

// assets
import deleteIcon from '~/assets/img/deleting.svg';

const PLATFORM_FILTER_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'ios', label: 'iOS' },
    { value: 'android', label: 'Android' },
    { value: 'pad-ios', label: 'Pad iOS' },
    { value: 'pad-android', label: 'Pad Android' },
];

class AppVersionsList extends Component {
    constructor(props) {
        super(props);
        this.state = {
            filters: { platform: '' },
            appliedFilters: { platform: '' },
        };
    }

    setFilter = (field, value) => {
        this.setState(prev => ({ filters: { ...prev.filters, [field]: value } }));
    };

    onFilter = () => {
        this.setState(prev => ({ appliedFilters: { ...prev.filters } }));
    };

    fetchVersions = (params = {}) => {
        const { appliedFilters } = this.state;
        return this.props.clientStore.getAppVersions({ ...params, platform: appliedFilters.platform || '' });
    };

    handleDeleteAction = (id, created_at) => {
        const { t, clientStore, commonStore } = this.props;
        
        const diff = moment().diff(moment(created_at), 'minutes');
        if (diff > 30) {
            return commonStore.addNotification(t('You can delete this within 30 minutes after creating it.'), null, 'warning');
        }

        confirmAlert({
            title: t('Confirm Deletion'),
            message: t('Are you sure that you want to delete this?'),
            buttons: [
                {
                    label: t('Yes'),
                    onClick: () => {
                        return clientStore.deleteAppVersion(id).then(() => {
                            commonStore.addNotification(t('Version removed successfully'), null, 'success');
                            clientStore.resetLastListLoadTime(new Date());
                        }).catch(err => {
                            commonStore.addNotification(err.message || t('Error deleting version'), null, 'error');
                        });
                    }
                },
                {
                    label: t('No'),
                    onClick: () => {}
                }
            ]
        });
    };

    renderTableActionButtons = (full) => {
        const { t } = this.props;
        const { id, created_at } = full;
        
        const isDeletable = moment().diff(moment(created_at), 'minutes') <= 30;

        return (
            <div>
                <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="delete-tooltip">{isDeletable ? t('Remove Version') : t('Deletion Expired')}</Tooltip>}
                >
                    <span>
                        <Button 
                            icon_sm_delete fill 
                            disabled={!isDeletable}
                            onClick={() => this.handleDeleteAction(id, created_at)}
                        >
                            <img 
                                src={deleteIcon} 
                                alt="delete" 
                                style={{ opacity: isDeletable ? 1 : 0.4 }} 
                            />
                        </Button>
                    </span>
                </OverlayTrigger>
            </div>
        );
    };

    renderHeader = () => {
        const { t } = this.props;
        return (
            <Row className="align-content-center mt-2 gx-0">
                <Col xs={12} className="text-end">
                    <Button fill wd onClick={() => this.props.router.navigate('/admin/app_versions/add')}>
                        <i className="fa fa-plus-circle fa-xl margin-right-10" />
                        {t('Add new')}
                    </Button>
                </Col>
            </Row>
        );
    }

    render() {
        const { t, clientStore } = this.props;
        const { loading } = clientStore;
        const { filters, appliedFilters } = this.state;
        const platformFilterOptions = PLATFORM_FILTER_OPTIONS.map(o => ({ value: o.value, label: t(o.label) }));
        return (
            <Fragment>
                <GenericList
                    header={
                        <GenericFilter
                            filters={filters}
                            setFilter={this.setFilter}
                            onFilter={this.onFilter}
                            loading={loading}
                            hasSearch={false}
                            hasCustomFilter
                            customFilterName={{ value: 'platform' }}
                            customFilterOptions={platformFilterOptions}
                        />
                    }
                    filters={appliedFilters}
                    columns={[
                        {
                            Header: t('Platform'),
                            id: 'platform',
                            accessor: ({ platform }) => (
                                <span className="text-capitalize">
                                    {platform}
                                </span>
                            ),
                            textAlign: 'start',
                            minWidth: 50,
                            maxWidth: 60,
                        },
                        {
                            Header: t('Version'),
                            accessor: 'version',
                            isSortable: true,
                            minWidth: 50,
                            maxWidth: 70,
                            textAlign: 'left',
                        },
                        {
                            Header: t('Force Update'),
                            id: 'force_update_required',
                            accessor: ({ force_update_required }) => (
                                <span>
                                    {force_update_required ? t('Yes') : t('No')}
                                </span>
                            ),
                            textAlign: 'left',
                            minWidth: 50,
                            maxWidth: 80,
                        },
                        {
                            Header: t('Title'),
                            id: 'title',
                            textAlign: 'start',
                            accessor: ({ title }) => (
                                <div className="text-truncate">
                                    {title}
                                </div>
                            ),
                            maxWidth: 150,
                        },
                        {
                            Header: t('Message'),
                            id: 'message',
                            textAlign: 'start',
                            accessor: ({ message }) => (
                                <div className="text-truncate">
                                    {message}
                                </div>
                            ),
                            maxWidth: 250,
                        },
                        {
                            Header: t('Created At'),
                            id: 'created_at',
                            accessor: ({ created_at }) => moment(created_at).format('YYYY-MM-DD HH:mm'),
                            maxWidth: 150,
                            isSortable: true,
                        },
                        {
                            Header: t('Actions'),
                            id: 'actions',
                            sortable: false,
                            minWidth: 50,
                            maxWidth: 80,
                            textAlign: 'left',
                            accessor: (full) => this.renderTableActionButtons(full),
                        },
                    ]}
                    tableHeader={t('App Versions')}
                    loading={loading}
                    lastListLoadTime={this.props.clientStore.lastListLoadTime}
                    requestData={(params) => this.fetchVersions(params)}
                >
                    {this.renderHeader()}
                </GenericList>
            </Fragment>
        );
    }
}

export default inject('clientStore', 'commonStore')(applicationRouter(withLocalization(observer(AppVersionsList))));